import { useState, useEffect, useCallback, useRef, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import {
  ArrowLeft, Upload, BarChart3, Users, Package, FileSearch,
  Trash2, AlertTriangle, TrendingUp, Calendar, Search, Database, ChevronRight,
  TrendingDown, Percent,
} from "lucide-react";

const eur = (n) =>
  (n ?? 0).toLocaleString("it-IT", { style: "currency", currency: "EUR" });
const num = (n, d = 0) => (n ?? 0).toLocaleString("it-IT", { minimumFractionDigits: d, maximumFractionDigits: d });
const date = (iso) => (iso ? new Date(iso).toLocaleDateString("it-IT") : "—");

const MONTH_NAMES = [
  "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre",
];

// Costruisce la query-string `&year=X&month=Y&cod_agente=Z&date_from=..&date_to=..`
// (vuota se nessun filtro). Se sono valorizzati date_from+date_to, year/month
// vengono comunque inviati per retro-compat (il backend dà priorità alle date).
const periodQS = (period) => {
  const parts = [];
  if (period?.date_from && period?.date_to) {
    parts.push(`date_from=${period.date_from}`);
    parts.push(`date_to=${period.date_to}`);
  } else {
    if (period?.year) {
      parts.push(`year=${period.year}`);
      if (period.month) parts.push(`month=${period.month}`);
    }
  }
  if (period?.cod_agente) parts.push(`cod_agente=${period.cod_agente}`);
  return parts.length ? "&" + parts.join("&") : "";
};

// Etichetta human-readable del filtro periodo applicato.
const periodLabel = (period, agentName) => {
  const parts = [];
  if (period?.date_from && period?.date_to) {
    parts.push(`${period.date_from} → ${period.date_to}`);
  } else if (period?.year) {
    parts.push(period.month ? `${MONTH_NAMES[period.month - 1]} ${period.year}` : `Anno ${period.year}`);
  }
  if (period?.cod_agente) parts.push(agentName || `Agente ${period.cod_agente}`);
  if (!parts.length) return "Tutti i periodi";
  return parts.join(" · ");
};

// Helper: filtra `items` confrontando ogni `field` in modo case-insensitive col `query`.
// Supporta sia stringhe che array di valori (per ricerche multi-campo).
const matchSearch = (item, fields, query) => {
  if (!query) return true;
  const q = query.toLowerCase().trim();
  if (!q) return true;
  return fields.some((f) => {
    const v = item?.[f];
    return v != null && String(v).toLowerCase().includes(q);
  });
};

// ============== SEARCH BOX ==============
function SearchBox({ value, onChange, placeholder = "Cerca...", testId = "search-box" }) {
  return (
    <div className="relative mb-3 max-w-md">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none" />
      <Input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        className="bg-zinc-900 border-zinc-700 text-zinc-100 placeholder:text-zinc-600 pl-9 h-9 text-sm focus:border-cyan-500"
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange("")}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-200 text-xs px-2"
          data-testid={`${testId}-clear`}
        >
          ✕
        </button>
      )}
    </div>
  );
}

export default function AdminSalesStats() {
  const { authAxios } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [period, setPeriod] = useState({ year: null, month: null });
  const [periods, setPeriods] = useState({ years: [], months_by_year: {} });
  const [agents, setAgents] = useState([]);
  // Tiene traccia se abbiamo già impostato il default "anno in corso", così
  // non sovrascriviamo una scelta esplicita dell'utente sui successivi refresh.
  const [defaultApplied, setDefaultApplied] = useState(false);

  // Carica anni/mesi disponibili una sola volta (e quando cambiano gli import).
  const refreshPeriods = useCallback(async () => {
    try {
      const r = await authAxios.get("/admin/sales-stats/periods");
      setPeriods(r.data);
      // Default: se l'anno in corso è presente negli import, selezionalo.
      // Altrimenti lascia "Tutti i periodi".
      if (!defaultApplied) {
        const currentYear = new Date().getFullYear();
        const years = r.data?.years || [];
        if (years.includes(currentYear)) {
          setPeriod((p) => ({ ...p, year: currentYear, month: null }));
        }
        setDefaultApplied(true);
      }
    } catch { /* ignore */ }
  }, [authAxios, defaultApplied]);

  // Carica lista agenti (per la Select filtro agente)
  const refreshAgents = useCallback(async () => {
    try {
      const r = await authAxios.get("/admin/sales-stats/agents");
      setAgents(r.data.items || []);
    } catch { /* ignore */ }
  }, [authAxios]);

  const refreshOverview = useCallback(async () => {
    try {
      const r = await authAxios.get(`/admin/sales-stats/overview?_=1${periodQS(period)}`);
      setOverview(r.data);
    } catch { /* ignore */ }
  }, [authAxios, period]);

  useEffect(() => { refreshOverview(); }, [refreshOverview]);
  useEffect(() => { refreshPeriods(); }, [refreshPeriods]);
  useEffect(() => { refreshAgents(); }, [refreshAgents]);

  return (
    <div className="min-h-screen bg-[#09090b] text-zinc-100">
      <header className="sticky top-0 z-30 border-b border-zinc-800 bg-[#09090b]/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3 flex items-center gap-3">
          <Button
            variant="ghost" size="sm" onClick={() => navigate("/admin")}
            data-testid="back-to-dashboard"
            className="text-zinc-400 hover:text-zinc-100"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <BarChart3 className="w-5 h-5 text-cyan-400" />
          <h1 className="text-base sm:text-lg font-bold tracking-tight">Statistiche Vendite</h1>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-3 sm:px-6 py-4 sm:py-6">
        {/* PERIOD FILTER */}
        <PeriodFilter period={period} setPeriod={setPeriod} periods={periods} agents={agents} />

        {/* OVERVIEW CARDS */}
        <OverviewCards overview={overview} period={period} />

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-6">
          <TabsList className="bg-zinc-900 border border-zinc-800 w-full grid grid-cols-2 sm:grid-cols-6 h-auto">
            <TabsTrigger value="overview" data-testid="tab-overview" className="text-xs sm:text-sm">
              <Users className="w-3.5 h-3.5 mr-1.5" /> Top Clienti
            </TabsTrigger>
            <TabsTrigger value="articles" data-testid="tab-articles" className="text-xs sm:text-sm">
              <Package className="w-3.5 h-3.5 mr-1.5" /> Top Articoli
            </TabsTrigger>
            <TabsTrigger value="margins" data-testid="tab-margins" className="text-xs sm:text-sm">
              <Percent className="w-3.5 h-3.5 mr-1.5" /> Marginalità
            </TabsTrigger>
            <TabsTrigger value="client-detail" data-testid="tab-client-detail" className="text-xs sm:text-sm">
              <FileSearch className="w-3.5 h-3.5 mr-1.5" /> Scheda Cliente
            </TabsTrigger>
            <TabsTrigger value="article-detail" data-testid="tab-article-detail" className="text-xs sm:text-sm">
              <Search className="w-3.5 h-3.5 mr-1.5" /> Confronto Articolo
            </TabsTrigger>
            <TabsTrigger value="imports" data-testid="tab-imports" className="text-xs sm:text-sm col-span-2 sm:col-span-1">
              <Database className="w-3.5 h-3.5 mr-1.5" /> Import PDF
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-4">
            <TopClientsView period={period} onPickClient={(code) => { setActiveTab("client-detail"); window.__pickClient = code; }} />
          </TabsContent>
          <TabsContent value="articles" className="mt-4">
            <TopArticlesView period={period} onPickArticle={(sku) => { setActiveTab("article-detail"); window.__pickArticle = sku; }} />
          </TabsContent>
          <TabsContent value="margins" className="mt-4">
            <MarginsView period={period}
                          onPickArticle={(sku) => { setActiveTab("article-detail"); window.__pickArticle = sku; }}
                          onPickClient={(code) => { setActiveTab("client-detail"); window.__pickClient = code; }} />
          </TabsContent>
          <TabsContent value="client-detail" className="mt-4">
            <ClientDetailView period={period} />
          </TabsContent>
          <TabsContent value="article-detail" className="mt-4">
            <ArticleDetailView period={period} />
          </TabsContent>
          <TabsContent value="imports" className="mt-4">
            <ImportsView onChanged={() => { refreshOverview(); refreshPeriods(); }} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

// ============== PERIOD FILTER (anno + mese + agente) ==============
function PeriodFilter({ period, setPeriod, periods, agents }) {
  const years = periods?.years || [];
  const monthsForYear = period.year ? (periods?.months_by_year?.[String(period.year)] || []) : [];
  const agentName = period.cod_agente
    ? (agents.find((a) => a.cod_agente === period.cod_agente)?.name || `Agente ${period.cod_agente}`)
    : null;

  const reset = () => setPeriod({ year: null, month: null, cod_agente: null, date_from: null, date_to: null });
  const usingRange = Boolean(period.date_from && period.date_to);

  return (
    <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="period-filter">
      <Calendar className="w-4 h-4 text-cyan-400 shrink-0" />
      <span className="text-xs text-zinc-500 uppercase tracking-wide">Periodo:</span>

      {/* ANNO */}
      <select
        value={period.year || ""}
        onChange={(e) => {
          const y = e.target.value ? parseInt(e.target.value, 10) : null;
          setPeriod({ ...period, year: y, month: null });
        }}
        data-testid="period-year-select"
        disabled={usingRange}
        className="bg-zinc-900 border border-zinc-700 text-zinc-100 text-xs sm:text-sm px-2 py-1.5 rounded focus:border-cyan-500 outline-none disabled:opacity-50"
      >
        <option value="">Tutti gli anni</option>
        {years.map((y) => (
          <option key={y} value={y}>{y}</option>
        ))}
      </select>

      {/* MESE (solo se è selezionato un anno) */}
      {period.year && !usingRange && (
        <select
          value={period.month || ""}
          onChange={(e) => {
            const m = e.target.value ? parseInt(e.target.value, 10) : null;
            setPeriod({ ...period, month: m });
          }}
          data-testid="period-month-select"
          className="bg-zinc-900 border border-zinc-700 text-zinc-100 text-xs sm:text-sm px-2 py-1.5 rounded focus:border-cyan-500 outline-none"
        >
          <option value="">Tutti i mesi</option>
          {monthsForYear.map((m) => (
            <option key={m} value={m}>{MONTH_NAMES[m - 1]}</option>
          ))}
        </select>
      )}

      {/* RANGE CUSTOM data → data */}
      <span className="text-[11px] text-zinc-600">oppure</span>
      <span className="text-[11px] text-zinc-500">Da</span>
      <input
        type="date"
        value={period.date_from || ""}
        max={period.date_to || undefined}
        onChange={(e) => setPeriod({ ...period, date_from: e.target.value || null })}
        data-testid="period-date-from"
        className="bg-zinc-900 border border-zinc-700 text-zinc-100 text-xs px-2 py-1 rounded focus:border-cyan-500 outline-none"
      />
      <span className="text-[11px] text-zinc-500">a</span>
      <input
        type="date"
        value={period.date_to || ""}
        min={period.date_from || undefined}
        onChange={(e) => setPeriod({ ...period, date_to: e.target.value || null })}
        data-testid="period-date-to"
        className="bg-zinc-900 border border-zinc-700 text-zinc-100 text-xs px-2 py-1 rounded focus:border-cyan-500 outline-none"
      />

      {/* AGENTE */}
      {agents.length > 0 && (
        <select
          value={period.cod_agente || ""}
          onChange={(e) => {
            const c = e.target.value ? parseInt(e.target.value, 10) : null;
            setPeriod({ ...period, cod_agente: c });
          }}
          data-testid="period-agent-select"
          className="bg-zinc-900 border border-zinc-700 text-zinc-100 text-xs sm:text-sm px-2 py-1.5 rounded focus:border-cyan-500 outline-none"
        >
          <option value="">Tutti gli agenti</option>
          {agents.map((a) => (
            <option key={a.cod_agente} value={a.cod_agente}>
              {a.name} ({a.n_clienti})
            </option>
          ))}
        </select>
      )}

      {(period.year || period.month || period.cod_agente || period.date_from || period.date_to) && (
        <Button
          variant="ghost" size="sm"
          onClick={reset}
          data-testid="period-reset-btn"
          className="text-xs text-zinc-400 hover:text-zinc-100 h-7 px-2"
        >
          Reset
        </Button>
      )}

      <span className="ml-auto text-[11px] text-zinc-500 font-mono">
        {periodLabel(period, agentName)}
      </span>
    </div>
  );
}

// ============== OVERVIEW CARDS ==============
function OverviewCards({ overview, period }) {
  if (!overview) {
    return <div className="text-zinc-500 text-sm">Caricamento...</div>;
  }
  const cards = [
    { label: "Fatturato totale", value: eur(overview.tot_eur), icon: TrendingUp, color: "text-emerald-400" },
    { label: "Pezzi/Kg venduti", value: num(overview.tot_qta, 0), icon: Package, color: "text-cyan-400" },
    { label: "Clienti attivi", value: overview.n_clients, icon: Users, color: "text-amber-400" },
    { label: "Articoli venduti", value: overview.n_articles, icon: BarChart3, color: "text-fuchsia-400" },
  ];
  return (
    <div data-testid="overview-cards">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3">
        {cards.map((c) => (
          <Card key={c.label} className="bg-zinc-900 border-zinc-800 p-3 sm:p-4">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] sm:text-xs text-zinc-500 uppercase tracking-wide">{c.label}</span>
              <c.icon className={`w-3.5 h-3.5 ${c.color}`} />
            </div>
            <div className={`text-base sm:text-2xl font-bold ${c.color}`}>{c.value}</div>
          </Card>
        ))}
      </div>
      <div className="mt-2 flex items-center gap-2 text-[11px] sm:text-xs text-zinc-500">
        <Calendar className="w-3 h-3" />
        <span>
          {period?.year ? (
            <>Filtrato: <strong className="text-cyan-300">{periodLabel(period)}</strong> · {date(overview.period_start)} → {date(overview.period_end)}</>
          ) : (
            <>Periodo: <strong className="text-zinc-300">{date(overview.period_start)} → {date(overview.period_end)}</strong> · {overview.n_imports} import attivi</>
          )}
        </span>
      </div>
    </div>
  );
}

// ============== TOP CLIENTS ==============
function TopClientsView({ period, onPickClient }) {
  const { authAxios } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const r = await authAxios.get(`/admin/sales-stats/top-clients?limit=100${periodQS(period)}`);
        if (!cancelled) setItems(r.data.items || []);
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [authAxios, period]);

  if (loading) return <div className="text-zinc-500 text-sm">Caricamento...</div>;
  if (!items.length) return <EmptyState />;

  const filtered = items.filter((c) =>
    matchSearch(c, ["denominazione", "codice_cli", "citta"], search)
  );

  return (
    <>
      <SearchBox
        value={search}
        onChange={setSearch}
        placeholder={`Cerca cliente in ${items.length} risultati (nome, codice, città)...`}
        testId="top-clients-search"
      />
      <Card className="bg-zinc-900 border-zinc-800 overflow-hidden" data-testid="top-clients-card">
      <div className="overflow-x-auto">
        <table className="w-full text-xs sm:text-sm">
          <thead className="bg-zinc-950/50 text-zinc-400 uppercase text-[10px] sm:text-xs">
            <tr>
              <th className="text-left px-3 py-2.5">#</th>
              <th className="text-left px-3 py-2.5">Cliente</th>
              <th className="text-right px-3 py-2.5">Fatturato</th>
              <th className="text-right px-3 py-2.5 hidden sm:table-cell">Qta</th>
              <th className="text-right px-3 py-2.5 hidden sm:table-cell">Articoli</th>
              <th className="text-right px-3 py-2.5 hidden md:table-cell">Dest.</th>
              <th className="text-right px-3 py-2.5 hidden md:table-cell">Ultimo</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {filtered.map((c, idx) => (
              <tr
                key={c.codice_cli}
                onClick={() => onPickClient(c.codice_cli)}
                className="hover:bg-zinc-800/50 cursor-pointer transition-colors"
                data-testid={`top-client-row-${c.codice_cli}`}
              >
                <td className="px-3 py-2 text-zinc-500 font-mono">{idx + 1}</td>
                <td className="px-3 py-2">
                  <div className="font-semibold text-zinc-100 truncate max-w-[180px] sm:max-w-none">
                    {c.denominazione || c.codice_cli}
                  </div>
                  <div className="text-[10px] sm:text-xs text-zinc-500 font-mono">
                    {c.codice_cli}{c.citta ? ` · ${c.citta}` : ""}
                  </div>
                </td>
                <td className="px-3 py-2 text-right font-mono font-bold text-emerald-400">{eur(c.tot_eur)}</td>
                <td className="px-3 py-2 text-right hidden sm:table-cell text-zinc-300 font-mono">{num(c.tot_qta)}</td>
                <td className="px-3 py-2 text-right hidden sm:table-cell text-zinc-400">{c.n_articles}</td>
                <td className="px-3 py-2 text-right hidden md:table-cell text-zinc-400">{c.n_destinations}</td>
                <td className="px-3 py-2 text-right hidden md:table-cell text-zinc-400">{date(c.last_purchase)}</td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="text-center text-zinc-500 py-6 text-xs">Nessun cliente trovato per &ldquo;{search}&rdquo;</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
    </>
  );
}

// ============== TOP ARTICLES ==============
function TopArticlesView({ period, onPickArticle }) {
  const { authAxios } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("eur");
  const [search, setSearch] = useState("");

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authAxios.get(`/admin/sales-stats/top-articles?limit=100&sort_by=${sortBy}${periodQS(period)}`);
      setItems(r.data.items || []);
    } finally { setLoading(false); }
  }, [authAxios, sortBy, period]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const filtered = items.filter((a) =>
    matchSearch(a, ["product_name", "descr", "sku", "macro_category"], search)
  );

  return (
    <div>
      <div className="flex items-center gap-2 mb-3 text-xs">
        <span className="text-zinc-500">Ordina per:</span>
        <Button
          variant={sortBy === "eur" ? "default" : "outline"}
          size="sm"
          onClick={() => setSortBy("eur")}
          data-testid="sort-by-eur"
          className={sortBy === "eur" ? "bg-cyan-600 hover:bg-cyan-700" : ""}
        >€ Fatturato</Button>
        <Button
          variant={sortBy === "qta" ? "default" : "outline"}
          size="sm"
          onClick={() => setSortBy("qta")}
          data-testid="sort-by-qta"
          className={sortBy === "qta" ? "bg-cyan-600 hover:bg-cyan-700" : ""}
        >Qta venduta</Button>
      </div>
      <SearchBox
        value={search}
        onChange={setSearch}
        placeholder={`Cerca articolo in ${items.length} risultati (nome, SKU, categoria)...`}
        testId="top-articles-search"
      />
      {loading ? <div className="text-zinc-500 text-sm">Caricamento...</div> : !items.length ? <EmptyState /> : (
        <Card className="bg-zinc-900 border-zinc-800 overflow-hidden" data-testid="top-articles-card">
          <div className="overflow-x-auto">
            <table className="w-full text-xs sm:text-sm">
              <thead className="bg-zinc-950/50 text-zinc-400 uppercase text-[10px] sm:text-xs">
                <tr>
                  <th className="text-left px-3 py-2.5">#</th>
                  <th className="text-left px-3 py-2.5">Articolo</th>
                  <th className="text-right px-3 py-2.5">Fatturato</th>
                  <th className="text-right px-3 py-2.5 hidden sm:table-cell">Qta</th>
                  <th className="text-right px-3 py-2.5 hidden sm:table-cell">€/pz medio</th>
                  <th className="text-right px-3 py-2.5 hidden md:table-cell">Clienti</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800">
                {filtered.map((a, idx) => (
                  <tr
                    key={a.sku}
                    onClick={() => onPickArticle(a.sku)}
                    className="hover:bg-zinc-800/50 cursor-pointer transition-colors"
                    data-testid={`top-article-row-${a.sku}`}
                  >
                    <td className="px-3 py-2 text-zinc-500 font-mono">{idx + 1}</td>
                    <td className="px-3 py-2">
                      <div className="font-semibold text-zinc-100 truncate max-w-[180px] sm:max-w-none">
                        {a.product_name || a.descr}
                      </div>
                      <div className="text-[10px] sm:text-xs text-zinc-500 font-mono">
                        SKU {a.sku}{a.macro_category ? ` · ${a.macro_category}` : ""}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right font-mono font-bold text-emerald-400">{eur(a.tot_eur)}</td>
                    <td className="px-3 py-2 text-right hidden sm:table-cell text-zinc-300 font-mono">{num(a.tot_qta, a.tot_qta % 1 !== 0 ? 2 : 0)}</td>
                    <td className="px-3 py-2 text-right hidden sm:table-cell text-zinc-400 font-mono">{eur(a.prezzo_medio)}</td>
                    <td className="px-3 py-2 text-right hidden md:table-cell text-zinc-400">{a.n_clients}</td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={6} className="text-center text-zinc-500 py-6 text-xs">Nessun articolo trovato per &ldquo;{search}&rdquo;</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

// ============== CLIENT DETAIL ==============
function ClientDetailView({ period }) {
  const { authAxios } = useAuth();
  const [codiceCli, setCodiceCli] = useState(window.__pickClient || "");
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  const load = useCallback(async (code) => {
    if (!code) return;
    setLoading(true);
    try {
      const r = await authAxios.get(`/admin/sales-stats/client/${encodeURIComponent(code)}?_=1${periodQS(period)}`);
      setDetail(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cliente non trovato");
      setDetail(null);
    } finally { setLoading(false); }
  }, [authAxios, period]);

  useEffect(() => {
    if (window.__pickClient) {
      setCodiceCli(window.__pickClient);
      load(window.__pickClient);
      window.__pickClient = null;
    }
  }, [load]);

  // Ricarica se il cliente è già selezionato e cambia il periodo
  useEffect(() => {
    if (codiceCli && detail) {
      load(codiceCli);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  return (
    <div>
      <div className="flex gap-2 mb-4">
        <Input
          ref={inputRef}
          value={codiceCli}
          onChange={(e) => setCodiceCli(e.target.value.toUpperCase())}
          placeholder="Codice cliente (es. AA303534)"
          className="bg-zinc-900 border-zinc-700 text-zinc-100 max-w-xs uppercase"
          data-testid="client-search-input"
          onKeyDown={(e) => { if (e.key === "Enter") load(codiceCli); }}
        />
        <Button onClick={() => load(codiceCli)} className="bg-cyan-600 hover:bg-cyan-700" data-testid="client-search-btn">
          <Search className="w-4 h-4 mr-1.5" /> Cerca
        </Button>
      </div>

      {loading && <div className="text-zinc-500 text-sm">Caricamento...</div>}
      {!loading && detail && (
        <ClientDetailContent detail={detail} />
      )}
      {!loading && !detail && (
        <div className="text-zinc-500 text-sm">Inserisci un codice cliente per vedere la scheda dettagliata.</div>
      )}
    </div>
  );
}

function ClientDetailContent({ detail }) {
  const { client, dest_totals, dest_addresses, rows } = detail;
  const addrByCod = Object.fromEntries((dest_addresses || []).map((d) => [d.cod_dest, d]));

  return (
    <div className="space-y-4" data-testid="client-detail-content">
      {client && (
        <Card className="bg-zinc-900 border-zinc-800 p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div>
              <div className="text-xs text-zinc-500">{client.codice_cli}</div>
              <div className="text-base sm:text-lg font-bold text-zinc-100">{client.denominazione || client.codice_cli}</div>
              <div className="text-xs text-zinc-400">{client.indirizzo} · {client.citta} {client.provincia ? `(${client.provincia})` : ""}</div>
            </div>
            <div className="text-right">
              <div className="text-xs text-zinc-500">{dest_totals.length} destinazioni</div>
              <div className="text-lg font-bold text-emerald-400 font-mono">
                {eur(dest_totals.reduce((s, d) => s + (d.tot_eur || 0), 0))}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* DESTINAZIONI */}
      <div className="space-y-3">
        {/* Riepilogo totale cliente (solo se ha multiple destinazioni) */}
        {dest_totals.length > 1 && (
          <DestinationCard
            isClientTotal
            dt={{
              cod_dest: null,
              tot_qta: dest_totals.reduce((s, d) => s + (d.tot_qta || 0), 0),
              tot_eur: dest_totals.reduce((s, d) => s + (d.tot_eur || 0), 0),
            }}
            addr={null}
            destRows={rows}
          />
        )}
        {dest_totals.map((dt) => {
          const addr = addrByCod[dt.cod_dest];
          const destRows = rows.filter((r) => r.cod_dest === dt.cod_dest);
          return (
            <DestinationCard
              key={dt.cod_dest}
              dt={dt}
              addr={addr}
              destRows={destRows}
            />
          );
        })}
      </div>
    </div>
  );
}

/**
 * Aggrega le righe di una destinazione per SKU. Ogni articolo diventa una
 * sola riga "totale" (somma di tutti i lotti); cliccando la riga si espande
 * mostrando il dettaglio per singolo lotto di acquisto.
 */
function aggregateBySku(destRows) {
  const map = new Map();
  for (const r of destRows) {
    const key = r.sku;
    if (!map.has(key)) {
      map.set(key, {
        sku: r.sku,
        product_name: r.product_name,
        descr: r.descr,
        tot_qta: 0,
        tot_eur: 0,
        ultima_data: null,
        lots: [],  // dettaglio per lotto
      });
    }
    const agg = map.get(key);
    agg.tot_qta += r.tot_qta;
    agg.tot_eur += r.tot_eur;
    if (!agg.ultima_data || r.ultima_data > agg.ultima_data) {
      agg.ultima_data = r.ultima_data;
    }
    agg.lots.push(r);
  }
  // Prezzo medio ponderato + n_volte totale
  for (const agg of map.values()) {
    agg.prezzo_medio = agg.tot_qta > 0 ? agg.tot_eur / agg.tot_qta : 0;
    agg.n_volte_tot = agg.lots.reduce((s, l) => s + (l.n_volte || 0), 0);
    // Lotto principale = quello con maggiore qta_tot
    agg.lots.sort((a, b) => b.tot_qta - a.tot_qta);
    agg.lotto_principale = agg.lots[0]?.lotto_qty;
  }
  return Array.from(map.values()).sort((a, b) => b.tot_eur - a.tot_eur);
}

function DestinationCard({ dt, addr, destRows, isClientTotal = false }) {
  const articles = aggregateBySku(destRows);
  const [expanded, setExpanded] = useState(new Set());

  const toggle = (sku) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(sku)) next.delete(sku); else next.add(sku);
      return next;
    });
  };

  return (
    <Card
      className={`bg-zinc-900 overflow-hidden ${
        isClientTotal ? "border-cyan-600/50 ring-1 ring-cyan-500/20" : "border-zinc-800"
      }`}
      data-testid={isClientTotal ? "client-totals-card" : `dest-card-${dt.cod_dest}`}
    >
      <div className={`px-3 sm:px-4 py-2.5 border-b flex items-center justify-between gap-2 ${
        isClientTotal
          ? "bg-cyan-950/40 border-cyan-800/40"
          : "bg-zinc-950/60 border-zinc-800"
      }`}>
        <div className="min-w-0">
          <div className={`text-xs uppercase tracking-wide ${isClientTotal ? "text-cyan-400 font-semibold" : "text-zinc-500"}`}>
            {isClientTotal ? "Riepilogo totale cliente" : `Destinazione #${dt.cod_dest}`}
          </div>
          {!isClientTotal && (
            <>
              <div className="text-sm font-semibold text-zinc-100 truncate">
                {addr?.denominazione || addr?.indirizzo || "—"}
              </div>
              {addr && <div className="text-[10px] text-zinc-500 truncate">{addr.indirizzo} · {addr.citta}</div>}
            </>
          )}
          {isClientTotal && (
            <div className="text-sm font-semibold text-cyan-100">
              Tutte le destinazioni
            </div>
          )}
        </div>
        <div className="text-right shrink-0">
          <div className={`text-sm font-bold font-mono ${isClientTotal ? "text-cyan-300" : "text-emerald-400"}`}>{eur(dt.tot_eur)}</div>
          <div className="text-[10px] text-zinc-500">{num(dt.tot_qta)} pz · {articles.length} articoli</div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="text-zinc-500 uppercase text-[9px] sm:text-[10px]">
            <tr>
              <th className="text-left px-2.5 py-1.5 w-6"></th>
              <th className="text-left px-2.5 py-1.5">SKU</th>
              <th className="text-left px-2.5 py-1.5">Articolo</th>
              <th className="text-right px-2.5 py-1.5">Qta tot</th>
              <th className="text-right px-2.5 py-1.5 hidden sm:table-cell">Lotti</th>
              <th className="text-right px-2.5 py-1.5">€ tot</th>
              <th className="text-right px-2.5 py-1.5">€/pz</th>
              <th className="text-right px-2.5 py-1.5">Ultimo</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {articles.map((a) => {
              const isOpen = expanded.has(a.sku);
              const hasMultipleLots = a.lots.length > 1;
              return (
                <Fragment key={a.sku}>
                  <tr
                    onClick={() => hasMultipleLots && toggle(a.sku)}
                    className={`hover:bg-zinc-800/30 ${hasMultipleLots ? "cursor-pointer" : ""}`}
                    data-testid={`article-summary-${a.sku}`}
                  >
                    <td className="px-2 py-1.5 text-zinc-500">
                      {hasMultipleLots ? (
                        <ChevronRight className={`w-3.5 h-3.5 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                      ) : (
                        <span className="block w-3.5" />
                      )}
                    </td>
                    <td className="px-2.5 py-1.5 text-zinc-500 font-mono">{a.sku}</td>
                    <td className="px-2.5 py-1.5 text-zinc-200 truncate max-w-[140px] sm:max-w-none">{a.product_name || a.descr}</td>
                    <td className="px-2.5 py-1.5 text-right font-mono text-zinc-300 font-semibold">{num(a.tot_qta, a.tot_qta % 1 !== 0 ? 2 : 0)}</td>
                    <td className="px-2.5 py-1.5 text-right hidden sm:table-cell font-mono text-zinc-400">
                      {hasMultipleLots ? (
                        <span className="px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-300 text-[10px]">{a.lots.length} lotti</span>
                      ) : (
                        <span className="px-1.5 py-0.5 rounded bg-cyan-500/10 text-cyan-400 font-bold">{a.lotto_principale}</span>
                      )}
                    </td>
                    <td className="px-2.5 py-1.5 text-right font-mono font-bold text-emerald-400">{eur(a.tot_eur)}</td>
                    <td className="px-2.5 py-1.5 text-right font-mono text-zinc-300">{eur(a.prezzo_medio)}</td>
                    <td className="px-2.5 py-1.5 text-right text-zinc-400">{date(a.ultima_data)}</td>
                  </tr>
                  {isOpen && a.lots.map((l, li) => (
                    <tr
                      key={`${a.sku}-lot-${l.lotto_qty}-${li}`}
                      className="bg-zinc-950/30"
                      data-testid={`article-lot-${a.sku}-${l.lotto_qty}`}
                    >
                      <td className="px-2 py-1.5"></td>
                      <td className="px-2.5 py-1.5"></td>
                      <td className="px-2.5 py-1.5 text-[10px] text-zinc-500 pl-6">
                        ↳ lotto da {l.lotto_qty} pz × {num(l.n_volte, 0)} volte
                      </td>
                      <td className="px-2.5 py-1.5 text-right font-mono text-zinc-400 text-[11px]">{num(l.tot_qta, l.tot_qta % 1 !== 0 ? 2 : 0)}</td>
                      <td className="px-2.5 py-1.5 text-right hidden sm:table-cell">
                        <span className="px-1.5 py-0.5 rounded bg-cyan-500/10 text-cyan-400 font-bold text-[10px]">{l.lotto_qty}</span>
                      </td>
                      <td className="px-2.5 py-1.5 text-right font-mono text-emerald-400/70 text-[11px]">{eur(l.tot_eur)}</td>
                      <td className="px-2.5 py-1.5 text-right font-mono text-zinc-400 text-[11px]">{eur(l.prezzo_medio)}</td>
                      <td className="px-2.5 py-1.5 text-right text-zinc-500 text-[11px]">{date(l.ultima_data)}</td>
                    </tr>
                  ))}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ============== ARTICLE DETAIL ==============
function ArticleDetailView({ period }) {
  const { authAxios } = useAuth();
  const [sku, setSku] = useState(window.__pickArticle || "");
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (code) => {
    if (!code) return;
    setLoading(true);
    try {
      const r = await authAxios.get(`/admin/sales-stats/article/${encodeURIComponent(code)}?_=1${periodQS(period)}`);
      setDetail(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Articolo non trovato");
      setDetail(null);
    } finally { setLoading(false); }
  }, [authAxios, period]);

  useEffect(() => {
    if (window.__pickArticle) {
      setSku(window.__pickArticle);
      load(window.__pickArticle);
      window.__pickArticle = null;
    }
  }, [load]);

  // Ricarica se l'articolo è già selezionato e cambia il periodo
  useEffect(() => {
    if (sku && detail) {
      load(sku);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  return (
    <div>
      <div className="flex gap-2 mb-4">
        <Input
          value={sku}
          onChange={(e) => setSku(e.target.value.toUpperCase())}
          placeholder="Codice articolo (es. 42, E4040)"
          className="bg-zinc-900 border-zinc-700 text-zinc-100 max-w-xs"
          data-testid="article-search-input"
          onKeyDown={(e) => { if (e.key === "Enter") load(sku); }}
        />
        <Button onClick={() => load(sku)} className="bg-cyan-600 hover:bg-cyan-700" data-testid="article-search-btn">
          <Search className="w-4 h-4 mr-1.5" /> Cerca
        </Button>
      </div>

      {loading && <div className="text-zinc-500 text-sm">Caricamento...</div>}
      {!loading && detail && <ArticleDetailContent detail={detail} />}
      {!loading && !detail && (
        <div className="text-zinc-500 text-sm">Inserisci un codice articolo per vedere quali clienti lo comprano.</div>
      )}
    </div>
  );
}

function ArticleDetailContent({ detail }) {
  const { product, summary, rows } = detail;
  const name = product?.name || rows[0]?.descr || "—";

  return (
    <div className="space-y-4" data-testid="article-detail-content">
      <Card className="bg-zinc-900 border-zinc-800 p-3 sm:p-4">
        <div className="text-xs text-zinc-500">SKU {product?.sku || rows[0]?.sku || "—"}</div>
        <div className="text-base sm:text-lg font-bold text-zinc-100">{name}</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
          <Stat label="Pz/kg venduti" value={num(summary.tot_qta)} />
          <Stat label="Fatturato" value={eur(summary.tot_eur)} accent="text-emerald-400" />
          <Stat label="Clienti" value={summary.n_clients} />
          <Stat label="Lotto + frequente" value={summary.lotto_piu_frequente ?? "—"} accent="text-cyan-400" />
        </div>
        <div className="mt-2 text-[11px] text-zinc-500">
          Prezzo medio globale ponderato: <strong className="text-zinc-300 font-mono">{eur(summary.prezzo_medio_globale)}</strong>
        </div>
      </Card>

      <Card className="bg-zinc-900 border-zinc-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs sm:text-sm">
            <thead className="bg-zinc-950/50 text-zinc-400 uppercase text-[10px]">
              <tr>
                <th className="text-left px-3 py-2">Cliente</th>
                <th className="text-right px-3 py-2">Qta</th>
                <th className="text-right px-3 py-2">Lotto</th>
                <th className="text-right px-3 py-2 hidden sm:table-cell">N° volte</th>
                <th className="text-right px-3 py-2 hidden sm:table-cell">€/pz</th>
                <th className="text-right px-3 py-2">Fatturato</th>
                <th className="text-right px-3 py-2 hidden md:table-cell">Ultimo</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {rows.map((r, i) => (
                <tr key={`${r.codice_cli}-${r.lotto_qty}-${i}`} className="hover:bg-zinc-800/30">
                  <td className="px-3 py-2">
                    <div className="font-semibold text-zinc-200 truncate max-w-[160px] sm:max-w-none">{r.denominazione || r.codice_cli}</div>
                    <div className="text-[10px] text-zinc-500 font-mono">{r.codice_cli}</div>
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-zinc-300">{num(r.tot_qta, r.tot_qta % 1 !== 0 ? 2 : 0)}</td>
                  <td className="px-3 py-2 text-right font-mono">
                    <span className="px-1.5 py-0.5 rounded bg-cyan-500/10 text-cyan-400 font-bold">{r.lotto_qty}</span>
                  </td>
                  <td className="px-3 py-2 text-right font-mono hidden sm:table-cell text-zinc-400">{num(r.n_volte, 0)}</td>
                  <td className="px-3 py-2 text-right font-mono hidden sm:table-cell text-zinc-400">{eur(r.prezzo_medio)}</td>
                  <td className="px-3 py-2 text-right font-mono font-bold text-emerald-400">{eur(r.tot_eur)}</td>
                  <td className="px-3 py-2 text-right hidden md:table-cell text-zinc-400">{date(r.ultima_data)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function Stat({ label, value, accent = "text-zinc-100" }) {
  return (
    <div>
      <div className="text-[10px] uppercase text-zinc-500 tracking-wide">{label}</div>
      <div className={`text-base sm:text-lg font-bold font-mono ${accent}`}>{value}</div>
    </div>
  );
}

// ============== MARGINS VIEW ==============
function MarginsView({ period, onPickArticle, onPickClient }) {
  const { authAxios } = useAuth();
  const [subView, setSubView] = useState("articles"); // articles | clients | categories
  const [data, setData] = useState({ summary: null, items: [] });
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("margin_eur");
  const [search, setSearch] = useState("");
  // Filtro drill-in da "Per Categoria": stringa = nome categoria, "__none__" = senza categoria
  const [macroDrill, setMacroDrill] = useState(null);

  useEffect(() => {
    setLoading(true);
    setSearch(""); // reset filtro testuale al cambio sotto-vista
    const qs = periodQS(period);
    const macroQS = (subView === "articles" && macroDrill)
      ? `&macro_category=${encodeURIComponent(macroDrill)}` : "";
    const endpoint = subView === "articles" ? `/admin/sales-stats/margins?limit=2000&sort_by=${sortBy}${qs}${macroQS}`
                   : subView === "clients"  ? `/admin/sales-stats/margins/by-client?limit=200${qs}`
                   : subView === "categories" ? `/admin/sales-stats/margins/by-category?_=1${qs}`
                   : `/admin/sales-stats/margins/missing-catalog?_=1${qs}`;
    authAxios.get(endpoint)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [authAxios, subView, sortBy, period, macroDrill]);

  // Handler: click su una riga "Per Categoria" → drill in articoli
  const handleCategoryClick = (macro) => {
    // "—" significa "senza categoria" → useremo il sentinel "__none__" nella URL
    setMacroDrill(macro === "—" ? "__none__" : macro);
    setSubView("articles");
  };

  // Reset drill quando l'utente torna su "Per Categoria"
  const handleSubViewChange = (newSubView) => {
    if (newSubView !== "articles") setMacroDrill(null);
    setSubView(newSubView);
  };

  // Etichetta human readable del filtro categoria
  const macroDrillLabel = macroDrill === "__none__" ? "Senza categoria"
                       : macroDrill || null;

  // Campi su cui cercare in base alla sotto-vista
  const searchFields = subView === "articles" ? ["product_name", "sku", "descr", "macro_category"]
                    : subView === "clients"  ? ["denominazione", "codice_cli", "citta"]
                    : subView === "categories" ? ["macro_category"]
                    : ["sku", "descr"]; // missing
  const placeholder = subView === "articles" ? "Cerca articolo (nome, SKU, categoria)..."
                    : subView === "clients"  ? "Cerca cliente (nome, codice, città)..."
                    : subView === "categories" ? "Cerca categoria..."
                    : "Cerca SKU o descrizione...";
  const filteredItems = (data.items || []).filter((it) => matchSearch(it, searchFields, search));

  return (
    <div>
      {data.summary && subView === "articles" && (
        <Card className="bg-zinc-900 border-emerald-700/40 ring-1 ring-emerald-500/10 p-3 sm:p-4 mb-4" data-testid="margins-global-summary">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 sm:gap-3">
            <Stat label="Fatturato (con costo)" value={eur(data.summary.tot_revenue)} accent="text-zinc-100" />
            <Stat label="Costo acquisto" value={eur(data.summary.tot_cost)} accent="text-amber-400" />
            <Stat label="Margine lordo" value={eur(data.summary.margin_eur)} accent="text-emerald-400" />
            <Stat label="Margine %" value={`${(data.summary.margin_pct ?? 0).toFixed(1)}%`} accent="text-emerald-300" />
            <Stat label="Articoli analizzati" value={`${data.summary.n_articles_kpi}`} />
          </div>
          {data.summary.n_articles_no_cost > 0 && (
            <div className="mt-2 flex items-center gap-1.5 text-[11px] text-amber-400">
              <AlertTriangle className="w-3 h-3" />
              <span>{data.summary.n_articles_no_cost} articoli esclusi (senza costo anagrafico)</span>
            </div>
          )}
        </Card>
      )}

      <div className="flex flex-wrap gap-2 mb-3">
        {[
          { id: "articles", label: "Per Articolo", icon: Package },
          { id: "clients", label: "Per Cliente", icon: Users },
          { id: "categories", label: "Per Categoria", icon: BarChart3 },
          { id: "missing", label: "Mancanti", icon: AlertTriangle },
        ].map((sv) => (
          <Button
            key={sv.id}
            variant={subView === sv.id ? "default" : "outline"}
            size="sm"
            data-testid={`margins-subview-${sv.id}`}
            onClick={() => handleSubViewChange(sv.id)}
            className={subView === sv.id ? "bg-emerald-600 hover:bg-emerald-700" : ""}
          >
            <sv.icon className="w-3.5 h-3.5 mr-1.5" /> {sv.label}
          </Button>
        ))}
        {subView === "articles" && (
          <div className="ml-auto flex items-center gap-1.5 text-xs">
            <span className="text-zinc-500">Ordina:</span>
            {[
              { id: "margin_eur", label: "€ margine" },
              { id: "margin_pct", label: "% margine" },
              { id: "tot_eur", label: "Fatturato" },
            ].map((o) => (
              <Button key={o.id} variant={sortBy === o.id ? "default" : "outline"} size="sm"
                data-testid={`margins-sort-${o.id}`}
                onClick={() => setSortBy(o.id)}
                className={sortBy === o.id ? "bg-emerald-600 hover:bg-emerald-700" : ""}>
                {o.label}
              </Button>
            ))}
          </div>
        )}
      </div>

      {loading ? <div className="text-zinc-500 text-sm">Caricamento...</div> :
        !data.items?.length ? (
          <>
            {macroDrillLabel && (
              <div className="mb-3 flex items-center gap-2 px-3 py-2 bg-cyan-950/40 border border-cyan-700/40 rounded text-xs text-cyan-200" data-testid="macro-drill-banner-empty">
                <BarChart3 className="w-3.5 h-3.5" />
                <span>Filtrato per categoria: <strong>{macroDrillLabel}</strong></span>
                <button onClick={() => setMacroDrill(null)} className="ml-auto text-cyan-300 hover:text-cyan-100" data-testid="macro-drill-clear">✕ Rimuovi</button>
              </div>
            )}
            <EmptyState />
          </>
        ) : (
        <>
          {macroDrillLabel && subView === "articles" && (
            <div className="mb-3 flex items-center gap-2 px-3 py-2 bg-cyan-950/40 border border-cyan-700/40 rounded text-xs text-cyan-200" data-testid="macro-drill-banner">
              <BarChart3 className="w-3.5 h-3.5 shrink-0" />
              <span>Drill-in da Per Categoria: <strong>{macroDrillLabel}</strong> · {data.items.length} articoli</span>
              <button onClick={() => setMacroDrill(null)} className="ml-auto text-cyan-300 hover:text-cyan-100 font-semibold" data-testid="macro-drill-clear">✕ Rimuovi filtro</button>
            </div>
          )}
          <SearchBox
            value={search}
            onChange={setSearch}
            placeholder={`${placeholder} (${data.items.length} risultati)`}
            testId={`margins-search-${subView}`}
          />
        <Card className="bg-zinc-900 border-zinc-800 overflow-hidden" data-testid="margins-table-card">
          {subView === "missing" && (
            <div className="px-3 sm:px-4 py-2.5 bg-amber-950/30 border-b border-amber-800/40 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" />
              <div className="text-xs sm:text-sm text-amber-100">
                <strong>{data.n_missing}</strong> articoli venduti ma <strong>non presenti nel catalogo</strong> · fatturato totale escluso dai KPI: <strong className="font-mono">{eur(data.tot_missing_revenue)}</strong>
              </div>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-xs sm:text-sm">
              <thead className="bg-zinc-950/50 text-zinc-400 uppercase text-[10px]">
                {subView === "articles" && (
                  <tr>
                    <th className="text-left px-3 py-2">Articolo</th>
                    <th className="text-right px-3 py-2 hidden sm:table-cell">Qta</th>
                    <th className="text-right px-3 py-2">Fatturato</th>
                    <th className="text-right px-3 py-2 hidden sm:table-cell">Costo</th>
                    <th className="text-right px-3 py-2">Margine</th>
                    <th className="text-right px-3 py-2">%</th>
                  </tr>
                )}
                {subView === "clients" && (
                  <tr>
                    <th className="text-left px-3 py-2">Cliente</th>
                    <th className="text-right px-3 py-2 hidden sm:table-cell">Qta</th>
                    <th className="text-right px-3 py-2">Fatturato</th>
                    <th className="text-right px-3 py-2 hidden sm:table-cell">Costo</th>
                    <th className="text-right px-3 py-2">Margine</th>
                    <th className="text-right px-3 py-2">%</th>
                  </tr>
                )}
                {subView === "categories" && (
                  <tr>
                    <th className="text-left px-3 py-2">Categoria</th>
                    <th className="text-right px-3 py-2 hidden sm:table-cell">SKU</th>
                    <th className="text-right px-3 py-2">Fatturato</th>
                    <th className="text-right px-3 py-2 hidden sm:table-cell">Costo</th>
                    <th className="text-right px-3 py-2">Margine</th>
                    <th className="text-right px-3 py-2">%</th>
                  </tr>
                )}
                {subView === "missing" && (
                  <tr>
                    <th className="text-left px-3 py-2">SKU</th>
                    <th className="text-left px-3 py-2">Descrizione (dal PDF)</th>
                    <th className="text-right px-3 py-2 hidden sm:table-cell">Qta</th>
                    <th className="text-right px-3 py-2">Fatturato</th>
                    <th className="text-right px-3 py-2 hidden md:table-cell">Clienti</th>
                    <th className="text-right px-3 py-2">Costo unit. €</th>
                  </tr>
                )}
              </thead>
              <tbody className="divide-y divide-zinc-800">
                {filteredItems.map((it, idx) => (
                  <MarginRow
                    key={it.sku || it.codice_cli || it.macro_category || idx}
                    item={it}
                    subView={subView}
                    onPickArticle={onPickArticle}
                    onPickClient={onPickClient}
                    onPickCategory={handleCategoryClick}
                  />
                ))}
                {filteredItems.length === 0 && (
                  <tr><td colSpan={6} className="text-center text-zinc-500 py-6 text-xs">Nessun risultato per &ldquo;{search}&rdquo;</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
        </>
      )}
    </div>
  );
}

function MarginRow({ item, subView, onPickArticle, onPickClient, onPickCategory }) {
  const pctColor = item.margin_pct >= 30 ? "text-emerald-400"
                : item.margin_pct >= 15 ? "text-amber-400"
                : "text-red-400";
  const PctIcon = item.margin_pct >= 30 ? TrendingUp
                : item.margin_pct >= 15 ? Percent
                : TrendingDown;

  if (subView === "articles") {
    return (
      <tr
        onClick={() => onPickArticle?.(item.sku)}
        className="hover:bg-zinc-800/50 cursor-pointer"
        data-testid={`margin-article-row-${item.sku}`}
      >
        <td className="px-3 py-2">
          <div className="font-semibold text-zinc-100 truncate max-w-[160px] sm:max-w-none">{item.product_name}</div>
          <div className="text-[10px] text-zinc-500 font-mono">{item.sku}{item.macro_category ? ` · ${item.macro_category}` : ""}</div>
        </td>
        <td className="px-3 py-2 text-right hidden sm:table-cell font-mono text-zinc-300">{num(item.tot_qta, item.tot_qta % 1 !== 0 ? 2 : 0)}</td>
        <td className="px-3 py-2 text-right font-mono text-zinc-300">{eur(item.tot_eur)}</td>
        <td className="px-3 py-2 text-right hidden sm:table-cell font-mono text-amber-400">{eur(item.cost_tot)}</td>
        <td className="px-3 py-2 text-right font-mono font-bold text-emerald-400">{eur(item.margin_eur)}</td>
        <td className={`px-3 py-2 text-right font-mono font-bold ${pctColor}`}>
          <span className="inline-flex items-center gap-1 justify-end">
            <PctIcon className="w-3 h-3" />
            {item.margin_pct.toFixed(1)}%
          </span>
        </td>
      </tr>
    );
  }
  if (subView === "clients") {
    return (
      <tr
        onClick={() => onPickClient?.(item.codice_cli)}
        className="hover:bg-zinc-800/50 cursor-pointer"
        data-testid={`margin-client-row-${item.codice_cli}`}
      >
        <td className="px-3 py-2">
          <div className="font-semibold text-zinc-100 truncate max-w-[160px] sm:max-w-none">{item.denominazione || item.codice_cli}</div>
          <div className="text-[10px] text-zinc-500 font-mono">{item.codice_cli}</div>
        </td>
        <td className="px-3 py-2 text-right hidden sm:table-cell font-mono text-zinc-300">{num(item.tot_qta, 0)}</td>
        <td className="px-3 py-2 text-right font-mono text-zinc-300">{eur(item.tot_eur)}</td>
        <td className="px-3 py-2 text-right hidden sm:table-cell font-mono text-amber-400">{eur(item.tot_cost)}</td>
        <td className="px-3 py-2 text-right font-mono font-bold text-emerald-400">{eur(item.margin_eur)}</td>
        <td className={`px-3 py-2 text-right font-mono font-bold ${pctColor}`}>
          <span className="inline-flex items-center gap-1 justify-end">
            <PctIcon className="w-3 h-3" />
            {item.margin_pct.toFixed(1)}%
          </span>
        </td>
      </tr>
    );
  }
  // categories
  if (subView === "categories") {
    return (
      <tr
        onClick={() => onPickCategory?.(item.macro_category)}
        className="hover:bg-cyan-900/30 cursor-pointer transition-colors"
        data-testid={`margin-category-row-${item.macro_category}`}
      >
        <td className="px-3 py-2">
          <span className="font-semibold text-zinc-100">{item.macro_category}</span>
          <ChevronRight className="inline-block w-3.5 h-3.5 ml-1 text-cyan-400 opacity-60" />
        </td>
        <td className="px-3 py-2 text-right hidden sm:table-cell font-mono text-zinc-400">{item.n_sku}</td>
        <td className="px-3 py-2 text-right font-mono text-zinc-300">{eur(item.tot_eur)}</td>
        <td className="px-3 py-2 text-right hidden sm:table-cell font-mono text-amber-400">{eur(item.tot_cost)}</td>
        <td className="px-3 py-2 text-right font-mono font-bold text-emerald-400">{eur(item.margin_eur)}</td>
        <td className={`px-3 py-2 text-right font-mono font-bold ${pctColor}`}>
          <span className="inline-flex items-center gap-1 justify-end">
            <PctIcon className="w-3 h-3" />
            {item.margin_pct.toFixed(1)}%
          </span>
        </td>
      </tr>
    );
  }
  // missing (articoli venduti ma non a catalogo)
  return <MissingRow item={item} />;
}

function MissingRow({ item }) {
  const { authAxios } = useAuth();
  const [cost, setCost] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const onSave = async (e) => {
    e?.stopPropagation();
    const value = parseFloat(String(cost).replace(",", "."));
    if (!value || value <= 0) {
      toast.error("Inserisci un costo valido (> 0)");
      return;
    }
    setSaving(true);
    try {
      await authAxios.post(
        `/admin/sales-stats/margins/missing/${encodeURIComponent(item.sku)}/cost`,
        { cost: value },
      );
      setSaved(true);
      toast.success(`Costo €${value.toFixed(3)} salvato per ${item.sku}. Aggiorna i KPI per vederlo.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    } finally {
      setSaving(false);
    }
  };

  return (
    <tr className={`hover:bg-zinc-800/30 ${saved ? "opacity-50" : ""}`} data-testid={`margin-missing-row-${item.sku}`}>
      <td className="px-3 py-2 font-mono text-zinc-300">{item.sku}</td>
      <td className="px-3 py-2 text-zinc-100 truncate max-w-[200px] sm:max-w-none">{item.descr}</td>
      <td className="px-3 py-2 text-right hidden sm:table-cell font-mono text-zinc-300">{num(item.tot_qta, item.tot_qta % 1 !== 0 ? 2 : 0)}</td>
      <td className="px-3 py-2 text-right font-mono font-bold text-amber-400">{eur(item.tot_eur)}</td>
      <td className="px-3 py-2 text-right hidden md:table-cell text-zinc-400">{item.n_clients}</td>
      <td className="px-3 py-2">
        {saved ? (
          <span className="text-[11px] text-emerald-400 font-bold">✓ Salvato</span>
        ) : (
          <div className="flex items-center gap-1.5 justify-end">
            <input
              type="text"
              inputMode="decimal"
              value={cost}
              onChange={(e) => setCost(e.target.value)}
              placeholder="0,000"
              className="w-20 px-1.5 py-1 rounded bg-zinc-950 border border-zinc-700 text-right text-xs font-mono text-zinc-100 focus:border-emerald-500 outline-none"
              data-testid={`missing-cost-input-${item.sku}`}
              onKeyDown={(e) => { if (e.key === "Enter") onSave(e); }}
            />
            <Button
              size="sm"
              onClick={onSave}
              disabled={saving}
              data-testid={`missing-cost-save-${item.sku}`}
              className="h-7 px-2 bg-emerald-600 hover:bg-emerald-700 text-[11px]"
            >
              {saving ? "..." : "Salva"}
            </Button>
          </div>
        )}
      </td>
    </tr>
  );
}

function EmptyState() {
  return (
    <Card className="bg-zinc-900 border-zinc-800 p-6 sm:p-10 text-center">
      <BarChart3 className="w-10 h-10 mx-auto mb-3 text-zinc-700" />
      <p className="text-sm text-zinc-400">Nessun dato disponibile. Carica un PDF "Venduto per cliente-articolo" dalla tab "Import PDF".</p>
    </Card>
  );
}

// ============== IMPORTS VIEW ==============
function ImportsView({ onChanged }) {
  const { authAxios } = useAuth();
  const [imports, setImports] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const fetchImports = useCallback(async () => {
    try {
      const r = await authAxios.get("/admin/sales-stats/imports");
      setImports(r.data.imports || []);
    } catch { /* ignore */ }
  }, [authAxios]);

  useEffect(() => { fetchImports(); }, [fetchImports]);

  const doUpload = async (force = false) => {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Seleziona un PDF"); return; }
    setUploading(true);
    let jobId = null;
    const tid = toast.loading("Caricamento PDF…");
    try {
      // 1) POST rapida: uploadare il file e ottenere un job_id
      const fd = new FormData();
      fd.append("file", file);
      const r = await authAxios.post(
        `/admin/sales-stats/import?force=${force}`, fd,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 },
      );
      jobId = r.data.job_id;
      // 2) Polling ogni 2s
      let finished = false;
      let last = null;
      while (!finished) {
        await new Promise((res) => setTimeout(res, 2000));
        const s = await authAxios.get(`/admin/sales-stats/import-status/${jobId}`);
        last = s.data;
        // aggiorna toast con progresso
        if (last.status === "parsing") {
          toast.loading(last.stage || "Lettura PDF…", { id: tid });
        } else if (last.status === "inserting") {
          const pct = last.n_total ? Math.round((last.n_inserted / last.n_total) * 100) : 0;
          toast.loading(`Inserimento righe: ${last.n_inserted}/${last.n_total} (${pct}%)`, { id: tid });
        } else {
          finished = true;
        }
      }
      // 3) Gestione esiti
      if (last.status === "overlap") {
        toast.dismiss(tid);
        if (window.confirm(
          `Sovrapposizione periodo ${last.preview?.period_start} → ${last.preview?.period_end} con ${last.overlapping_imports?.length || 0} import esistenti:\n${(last.overlapping_imports || []).map((o) => `· ${o.filename} (${o.period_start} → ${o.period_end})`).join("\n")}\n\nImportare comunque?`
        )) {
          // Riprendi con force
          const rf = await authAxios.post(`/admin/sales-stats/import-confirm-force/${jobId}`);
          jobId = rf.data.job_id;
          const tid2 = toast.loading("Ripresa import (force)…");
          let done2 = false;
          let last2 = null;
          while (!done2) {
            await new Promise((res) => setTimeout(res, 2000));
            const s2 = await authAxios.get(`/admin/sales-stats/import-status/${jobId}`);
            last2 = s2.data;
            if (last2.status === "parsing") toast.loading(last2.stage || "Lettura PDF…", { id: tid2 });
            else if (last2.status === "inserting") {
              const pct = last2.n_total ? Math.round((last2.n_inserted / last2.n_total) * 100) : 0;
              toast.loading(`Inserimento righe: ${last2.n_inserted}/${last2.n_total} (${pct}%)`, { id: tid2 });
            } else done2 = true;
          }
          if (last2.status === "done") {
            toast.success(`Importate ${last2.result?.n_inserted || 0} righe (${last2.result?.import?.n_clients || 0} clienti)`, { id: tid2 });
            if (fileRef.current) fileRef.current.value = "";
            await fetchImports();
            onChanged?.();
          } else {
            toast.error(last2.error || "Errore durante l'import", { id: tid2 });
          }
        } else {
          // Utente annulla → cancella job
          try { await authAxios.delete(`/admin/sales-stats/import-cancel/${jobId}`); } catch { /* ignore */ }
        }
      } else if (last.status === "done") {
        toast.success(`Importate ${last.result?.n_inserted || 0} righe (${last.result?.import?.n_clients || 0} clienti)`, { id: tid });
        if (fileRef.current) fileRef.current.value = "";
        await fetchImports();
        onChanged?.();
      } else if (last.status === "failed") {
        toast.error(last.error || "Errore durante l'import", { id: tid });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore durante l'import", { id: tid });
    } finally { setUploading(false); }
  };

  const doDelete = async (id, filename) => {
    if (!window.confirm(`Annullare l'import "${filename}"? Le righe associate verranno cancellate.`)) return;
    try {
      await authAxios.delete(`/admin/sales-stats/imports/${id}`);
      toast.success("Import annullato");
      await fetchImports();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  return (
    <div className="space-y-4">
      <Card className="bg-zinc-900 border-zinc-800 p-3 sm:p-4">
        <div className="text-sm font-semibold text-zinc-100 mb-2 flex items-center gap-2">
          <Upload className="w-4 h-4 text-cyan-400" /> Carica nuovo PDF
        </div>
        <p className="text-xs text-zinc-500 mb-3">
          PDF "Venduto Giornaliero per cliente-articolo". Periodi sovrapposti vengono rilevati automaticamente.
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            ref={fileRef}
            type="file"
            accept="application/pdf"
            data-testid="import-file-input"
            className="text-xs text-zinc-300 file:mr-2 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-zinc-800 file:text-zinc-300 file:cursor-pointer flex-1"
          />
          <Button onClick={() => doUpload(false)} disabled={uploading} className="bg-cyan-600 hover:bg-cyan-700" data-testid="upload-pdf-btn">
            {uploading ? "Caricamento..." : "Carica PDF"}
          </Button>
        </div>
      </Card>

      <Card className="bg-zinc-900 border-zinc-800 overflow-hidden">
        <div className="px-3 sm:px-4 py-2.5 border-b border-zinc-800 bg-zinc-950/60 text-xs uppercase text-zinc-400">
          Import storici ({imports.length})
        </div>
        {!imports.length ? (
          <div className="p-6 text-center text-zinc-500 text-sm">Nessun PDF importato.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs sm:text-sm">
              <thead className="bg-zinc-950/40 text-zinc-400 uppercase text-[10px]">
                <tr>
                  <th className="text-left px-3 py-2">File</th>
                  <th className="text-left px-3 py-2">Periodo</th>
                  <th className="text-right px-3 py-2 hidden sm:table-cell">Righe</th>
                  <th className="text-right px-3 py-2 hidden md:table-cell">Clienti</th>
                  <th className="text-left px-3 py-2 hidden md:table-cell">Importato</th>
                  <th className="text-left px-3 py-2">Stato</th>
                  <th className="text-right px-3 py-2">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800">
                {imports.map((imp) => (
                  <tr key={imp.id} data-testid={`import-row-${imp.id}`}>
                    <td className="px-3 py-2 font-mono text-xs text-zinc-300 truncate max-w-[160px]">{imp.filename}</td>
                    <td className="px-3 py-2 text-zinc-300 whitespace-nowrap">{date(imp.period_start)} → {date(imp.period_end)}</td>
                    <td className="px-3 py-2 text-right hidden sm:table-cell font-mono text-zinc-300">{num(imp.n_rows)}</td>
                    <td className="px-3 py-2 text-right hidden md:table-cell font-mono text-zinc-400">{imp.n_clients}</td>
                    <td className="px-3 py-2 hidden md:table-cell text-[10px] text-zinc-500">{date(imp.imported_at)}</td>
                    <td className="px-3 py-2">
                      {imp.status === "active" ? (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 font-mono">attivo</span>
                      ) : (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-700/40 text-zinc-500 font-mono line-through">eliminato</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {imp.status === "active" && (
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => doDelete(imp.id, imp.filename)}
                          data-testid={`delete-import-${imp.id}`}
                          className="text-zinc-400 hover:text-red-400 h-7 w-7 p-0"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
